import React from 'react'
import {
  Box,
  Typography,
  Grid2 as Grid,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Alert,
  useTheme
} from '@mui/material'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import { ISymptomsCategoryResponse, ISymptonsResponse } from 'types'
import ButtonBase from 'design-system/button'

interface SymptomsFormProps {
  step: 'category' | 'symptoms'
  categories: ISymptomsCategoryResponse[]
  selectedCategory: ISymptomsCategoryResponse | null
  symptoms: ISymptonsResponse[]
  selectedSymptoms: string[]
  symptomDate: Dayjs | null
  symptomTime: Dayjs | null
  loadingCategories: boolean
  loadingSymptoms: boolean
  error: string | null
  onCategorySelect: (category: ISymptomsCategoryResponse) => void
  onSymptomToggle: (symptomId: string) => void
  onBackToCategories: () => void
  onDateChange: (date: Dayjs | null) => void
  onTimeChange: (time: Dayjs | null) => void
  onAdvanceToSymptoms: () => void
}

const SymptomsForm: React.FC<SymptomsFormProps> = ({
  step,
  categories,
  selectedCategory,
  symptoms,
  selectedSymptoms,
  symptomDate,
  symptomTime,
  loadingCategories,
  loadingSymptoms,
  error,
  onCategorySelect,
  onSymptomToggle,
  onBackToCategories,
  onDateChange,
  onTimeChange,
  onAdvanceToSymptoms
}) => {
  const theme = useTheme()

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
        {step === 'symptoms' && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBackToCategories}
            sx={{ mb: 2 }}
          >
            Voltar às categorias
          </Button>
        )}
      </Box>
    )
  }

  if (step === 'category') {
    return (
      <Box sx={{ p: 2 }}>
        <Typography
          variant='h6'
          sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}
        >
          Qual tipo de sintoma se adequa ao que o paciente tem?
        </Typography>

        {loadingCategories ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant='body2' color='text.secondary'>
              Carregando categorias...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {categories.map(category => (
              <Card
                key={category.symptoms_category_id}
                sx={{
                  border:
                    selectedCategory?.symptoms_category_id ===
                    category.symptoms_category_id
                      ? `2px solid ${theme.palette.selectedBorder.main}`
                      : `1px solid ${theme.palette.defaultBorder.main}`,
                  '&:hover': {
                    boxShadow: 2
                  }
                }}
              >
                <CardActionArea
                  onClick={() => onCategorySelect(category)}
                  disabled={loadingSymptoms}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant='body1' sx={{ fontWeight: 'medium' }}>
                      {category.name}
                    </Typography>
                    {category.description && (
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mt: 0.5 }}
                      >
                        {category.description}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}

        {loadingSymptoms && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              Carregando sintomas...
            </Typography>
          </Box>
        )}

        {!loadingSymptoms && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <ButtonBase
              variant='contained'
              onClick={onAdvanceToSymptoms}
              sx={{ minWidth: 120 }}
              disabled={!selectedCategory}
            >
              Avançar
            </ButtonBase>
          </Box>
        )}
      </Box>
    )
  }

  // Symptoms selection step
  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          onClick={onBackToCategories}
          size='small'
          startIcon={<ArrowBackIcon />}
          sx={{
            color: 'text.secondary'
          }}
        >
          {selectedCategory?.name}
        </Button>
      </Box>

      <Typography
        variant='h6'
        sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}
      >
        Quais sintomas se adequam ao que o paciente tem?
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        {symptoms.map(symptom => (
          <Card
            key={symptom.symptoms_id}
            sx={{
              border: selectedSymptoms.includes(symptom.symptoms_id)
                ? `2px solid ${theme.palette.selectedBorder.main}`
                : `1px solid ${theme.palette.defaultBorder.main}`,
              '&:hover': {
                boxShadow: 2
              }
            }}
          >
            <CardActionArea
              onClick={() => onSymptomToggle(symptom.symptoms_id)}
            >
              <CardContent sx={{ py: 2 }}>
                <Typography variant='body1' sx={{ fontWeight: 'medium' }}>
                  {symptom.name}
                </Typography>
                {symptom.description && (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mt: 0.5 }}
                  >
                    {symptom.description}
                  </Typography>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      {/* Date and Time Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant='h6' sx={{ mb: 2, fontWeight: 'bold' }}>
          Quando os sintomas começaram?
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <DatePicker
                label='Data'
                value={symptomDate}
                onChange={newValue => onDateChange(newValue)}
                format='DD/MM/YYYY'
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TimePicker
                label='Hora'
                value={symptomTime}
                onChange={newValue => onTimeChange(newValue)}
                format='HH:mm'
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
    </Box>
  )
}

export default SymptomsForm
